import type { FC } from 'react'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import type { UIButtonProps } from 'src/components/ui/Button'
import { UIButton } from 'src/components/ui/Button'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import type { Task } from 'src/services/apollo/generated/graphql'
import {
  executeCancelGenerationTask,
  executeRerunGenerationTask,
} from 'src/services/apollo/task'
import { deleteTask, deleteTaskMedia } from 'src/services/helpers/task'

/**
 * Cancel a waiting task without refund.
 * For tasks that have not been running for 24 hours yet.
 */
export const CancelWithoutRefundButton: FC<{
  task: Task
  className?: string
}> = ({ task, className }) => {
  const { t } = useTranslation(['artwork'])
  return (
    <UIButton
      className={className}
      variant="filled"
      onPress={async () => {
        if (!task) return

        const toProceed = await UIDialog.confirm({
          heading: <Text i18nKey="artwork:task.cancel.title" />,
          content: <Text i18nKey="artwork:task.cancel.info" />,
          cancelProps: {
            children: <Text i18nKey="action:no" />,
          },
          confirmProps: {
            children: <Text i18nKey="action:yes" />,
          },
        })
        if (!toProceed) return

        try {
          await executeCancelGenerationTask({ taskId: task.id })
          toast.info('Task cancelled')
        } catch (e: any) {
          toast.error(e?.message)
        }
      }}
    >
      {t('artwork:cancel-generation')}
    </UIButton>
  )
}

export interface DeleteTaskButtonProps extends UIButtonProps {
  task: Task
  onSuccess?: () => any
  onBeforeClick?: () => any
}

/**
 * Delete a task permanently.
 * Shown in task detail dialog footer when the task is not cancelable.
 */
export const DeleteTaskButton = forwardRef<
  HTMLButtonElement,
  DeleteTaskButtonProps
>(function DeleteTaskButton(
  { task, onBeforeClick, onSuccess = () => {}, children, ...props },
  ref,
) {
  const [{ loading }, doDeleteTask] = useAsyncFn(
    async (taskToDelete: Task) => {
      if (await deleteTask(taskToDelete)) {
        onSuccess()
      }
    },
    [onSuccess],
  )

  return (
    <UIButton
      onPress={() => {
        onBeforeClick?.()
        doDeleteTask(task)
      }}
      busy={loading}
      ref={ref}
      {...props}
    >
      {children}
    </UIButton>
  )
})

export const DeleteTaskMediaBtn = forwardRef<
  HTMLButtonElement,
  UIButtonProps & {
    task: Task
    onTaskDelete?: () => any
    onMediaDelete?: (mediaId?: string) => any
    onCancel?: () => any
    mediaId?: string
  }
>(function DeleteTaskMediaBtn(
  { task, onMediaDelete, onTaskDelete, onCancel, mediaId, children, ...props },
  ref,
) {
  const { t } = useTranslation(['artwork'])

  const [{ loading }, doDeleteTaskMedia] = useAsyncFn(
    async (taskToDelete: Task, mediaIdToDelete?: string) => {
      let taskBeDeleted = !mediaIdToDelete
      const result = taskBeDeleted
        ? await deleteTask(taskToDelete)
        : await deleteTaskMedia(
            taskToDelete,
            mediaIdToDelete!,
            shouldDeleteTask => {
              taskBeDeleted = shouldDeleteTask
            },
          )
      if (result) {
        if (taskBeDeleted) onTaskDelete?.()
        else onMediaDelete?.(mediaIdToDelete)
      } else if (result == null) {
        onCancel?.()
      }
    },
    [onTaskDelete, onMediaDelete, onCancel],
  )

  return (
    <UIButton
      variant="filled"
      onPress={() => doDeleteTaskMedia(task, mediaId)}
      busy={loading}
      ref={ref}
      {...props}
    >
      {children || t('artwork:delete-media.label')}
    </UIButton>
  )
})

export const RerunTaskButton: FC<{ task: Task; onSuccess?: () => any }> = ({
  task,
  onSuccess = () => {},
}) => {
  const { t } = useTranslation(['artwork'])

  const [{ loading }, onClick] = useAsyncFn(async () => {
    const toProceed = await UIDialog.confirm({
      heading: <Text i18nKey="artwork:task.rerun.title" />,
      content: <Text i18nKey="artwork:task.rerun.info" />,
      cancelProps: {
        children: <Text i18nKey="action:no" />,
      },
      confirmProps: {
        children: <Text i18nKey="action:yes" />,
      },
    })
    if (!toProceed) return

    try {
      await executeRerunGenerationTask({ taskId: task.id })
      onSuccess()
      toast.success(t('artwork:task.rerun.success'))
    } catch (e: any) {
      toast.error(e?.message)
    }
  }, [task])

  return (
    <UIButton variant="filled" onPress={onClick} busy={loading}>
      {t('artwork:rerun-task')}
    </UIButton>
  )
}
